import { useCallback, useState } from 'react';
import { Scale } from 'lucide-react';
import { reconReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, fmtDate, fmtAmt, fmtAmtZero, Section, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import {
  Pill, Pager, Dash, InfoBanner, errMessage, DEFAULT_PAGE_SIZE,
} from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type { PagedResult } from '@/types/api.types';
import type {
  SubledgerSummaryDto, SubledgerSummaryRow, SubledgerDetailRow, SubledgerDetailFilter,
} from '@/types/reportCentre.types';

const REPORT_KEY = 'SUBLEDGER_RECON';

/** Areas that support document-level drill-down. */
const DETAIL_AREAS = new Set(['AR', 'AP']);

const AREA_LABELS: Record<string, string> = {
  AR:         'Accounts Receivable',
  AP:         'Accounts Payable',
  TAX_INPUT:  'Tax — Input',
  TAX_OUTPUT: 'Tax — Output',
  BANK:       'Bank',
};

const STATUS_TONES: Record<string, PillTone> = {
  MATCHED:        'green',
  DIFFERENCE:     'red',
  NOT_CONFIGURED: 'grey',
  INFO_ONLY:      'blue',
};

const MATCH_STATUS_OPTIONS = [
  { value: '',                     label: 'All Match Statuses' },
  { value: 'FULLY_MATCHED',        label: 'Fully Matched' },
  { value: 'AMOUNT_DIFFERENCE',    label: 'Amount Difference' },
  { value: 'MISSING_IN_GL',        label: 'Missing in GL' },
  { value: 'MISSING_IN_SUBLEDGER', label: 'Missing in Subledger' },
];

const MATCH_TONES: Record<string, PillTone> = {
  FULLY_MATCHED:        'green',
  AMOUNT_DIFFERENCE:    'red',
  MISSING_IN_GL:        'amber',
  MISSING_IN_SUBLEDGER: 'amber',
};

const hasDifference = (diff: number | null) => diff !== null && Math.abs(diff) >= 0.01;

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Area',       header: 'Area',               width: 22 },
  { key: 'Accounts',   header: 'Control Accounts',   width: 26 },
  { key: 'Gl',         header: 'GL Balance',         width: 16, numFmt: '#,##0.00' },
  { key: 'Sub',        header: 'Subledger Balance',  width: 18, numFmt: '#,##0.00' },
  { key: 'Diff',       header: 'Difference',         width: 16, numFmt: '#,##0.00' },
  { key: 'Unposted',   header: 'Unposted Subledger', width: 18, numFmt: '#,##0.00' },
  { key: 'Status',     header: 'Status',             width: 16 },
  { key: 'LastRecon',  header: 'Last Reconciled',    width: 16 },
  { key: 'Notes',      header: 'Notes',              width: 40 },
];

function buildExportRows(rows: SubledgerSummaryRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Area:      AREA_LABELS[r.area] ?? r.area,
    Accounts:  r.controlAccountCodes ?? '',
    Gl:        r.glBalance ?? '',
    Sub:       r.subledgerBalance ?? '',
    Diff:      r.difference ?? '',
    Unposted:  r.unpostedSubledgerAmount ?? '',
    Status:    r.status,
    LastRecon: r.lastReconciledDate ?? '',
    Notes:     r.notes ?? '',
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const SubledgerReconPage = () => {
  const { hasPermission } = usePermission();

  const [asAt, setAsAt] = useState(today());
  const [data, setData] = useState<SubledgerSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drill-down detail state
  const [detailArea, setDetailArea] = useState<string | null>(null);
  const [matchStatus, setMatchStatus] = useState('');
  const [detail, setDetail] = useState<PagedResult<SubledgerDetailRow> | null>(null);
  const [detailApplied, setDetailApplied] = useState<SubledgerDetailFilter | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDetail(null);
    setDetailArea(null);
    try {
      const res = await reconReportApi.getSubledgerSummary({ asAt });
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [asAt]);

  const fetchDetail = useCallback(async (filter: SubledgerDetailFilter) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await reconReportApi.getSubledgerDetail(filter);
      setDetail(res.data?.data ?? null);
      setDetailApplied(filter);
    } catch (e: unknown) {
      setDetailError(errMessage(e));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const drillInto = useCallback((area: string, status: string) => {
    if (!data) return;
    setDetailArea(area);
    void fetchDetail({
      area,
      asAt: data.asAt,
      matchStatus: status || undefined,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });
  }, [data, fetchDetail]);

  if (!hasPermission(Permissions.RPT_RECON)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const rows = data?.rows ?? [];
  const banks = data?.bankReconciliations ?? [];

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Subledger Recon',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(rows),
      title: 'Subledger vs GL Reconciliation',
      subtitle: `${data.companyName} · As at ${data.asAt}`,
    }, `SubledgerRecon_${data.asAt}`);
  };

  const handleCsv = () => {
    if (!data) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(rows), `SubledgerRecon_${data.asAt}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scale size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Subledger vs GL Reconciliation</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={rows.length}
          filterSummary={`asAt=${asAt}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="w-48">
            <label className={LABEL_CLS}>As At *</label>
            <input
              type="date"
              value={asAt}
              onChange={e => setAsAt(e.target.value)}
              className={INPUT_CLS}
            />
          </div>
          <RunReportButton onClick={() => { void run(); }} isLoading={loading} disabled={!asAt} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Pick an "As At" date and click "Run Report" to compare subledger balances against the GL.' />
      )}

      {data && (
        <div className="flex flex-col gap-4">
          {/* Report meta */}
          <div className={cn(CARD_CLS, 'px-4 py-3')}>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Subledger vs GL — as at {fmtDate(data.asAt)} · Generated {fmtDate(data.generatedAt)}
            </div>
          </div>

          {/* Area summary table */}
          <Section
            title="Reconciliation by Area"
            subtitle="Click an AR or AP row for document-level matching detail"
          >
            <div className="overflow-auto -m-4">
              <table className="w-full text-sm border-collapse min-w-[1000px]">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className={TH_CLS}>Area</th>
                    <th className={TH_CLS}>Control Accounts</th>
                    <th className={TH_R_CLS}>GL Balance</th>
                    <th className={TH_R_CLS}>Subledger Balance</th>
                    <th className={TH_R_CLS}>Difference</th>
                    <th className={TH_R_CLS}>Unposted</th>
                    <th className={cn(TH_CLS, 'w-32')}>Status</th>
                    <th className={cn(TH_CLS, 'w-28')}>Last Recon</th>
                    <th className={TH_CLS}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No reconciliation areas returned.
                      </td>
                    </tr>
                  ) : rows.map(r => {
                    const drillable = DETAIL_AREAS.has(r.area) && r.status !== 'NOT_CONFIGURED';
                    const diffBad = hasDifference(r.difference);
                    return (
                      <tr
                        key={r.area}
                        onClick={drillable ? () => drillInto(r.area, matchStatus) : undefined}
                        className={cn(
                          'border-t border-gray-100 dark:border-gray-800',
                          drillable && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
                          detailArea === r.area && 'bg-brand-50/50 dark:bg-brand-900/10',
                        )}
                        title={drillable ? 'Show document-level detail' : undefined}
                      >
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {AREA_LABELS[r.area] ?? r.area}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">
                          {r.controlAccountCodes ?? <Dash />}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                          {r.glBalance === null ? <Dash /> : fmtAmtZero(r.glBalance)}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                          {r.subledgerBalance === null ? <Dash /> : fmtAmtZero(r.subledgerBalance)}
                        </td>
                        <td className={cn(
                          AMT_CLS,
                          diffBad ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600',
                        )}>
                          {r.difference === null ? <Dash /> : fmtAmtZero(r.difference)}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                          {r.unpostedSubledgerAmount === null ? <Dash /> : fmtAmt(r.unpostedSubledgerAmount)}
                        </td>
                        <td className="px-3 py-2">
                          <Pill label={r.status} tone={STATUS_TONES[r.status] ?? 'grey'} />
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {r.lastReconciledDate ? fmtDate(r.lastReconciledDate) : <Dash />}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                          {r.notes ?? <Dash />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Bank reconciliation sub-table */}
          <Section title="Bank Reconciliations" subtitle="Latest reconciliation per bank account — informational">
            {banks.length === 0 ? (
              <InfoBanner>No bank reconciliations found for this company.</InfoBanner>
            ) : (
              <div className="overflow-auto -m-4">
                <table className="w-full text-sm border-collapse min-w-[1100px]">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className={TH_CLS}>Bank Account</th>
                      <th className={TH_CLS}>Account No</th>
                      <th className={TH_CLS}>GL Account</th>
                      <th className={TH_R_CLS}>GL Balance</th>
                      <th className={TH_CLS}>Last Recon No</th>
                      <th className={cn(TH_CLS, 'w-28')}>Statement Date</th>
                      <th className={cn(TH_CLS, 'w-28')}>Status</th>
                      <th className={TH_R_CLS}>Statement Closing</th>
                      <th className={TH_R_CLS}>Book Closing</th>
                      <th className={TH_R_CLS}>Difference</th>
                      <th className={cn(TH_CLS, 'w-28')}>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banks.map(b => {
                      const diffBad = hasDifference(b.reconciliationDifference);
                      return (
                        <tr key={b.bankAccountId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{b.bankAccountName}</td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{b.accountNumber}</td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{b.glAccountCode ?? <Dash />}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                            {b.glBalance === null ? <Dash /> : fmtAmtZero(b.glBalance)}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">
                            {b.lastReconciliationNumber ?? <Dash />}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {b.lastStatementDate ? fmtDate(b.lastStatementDate) : <Dash />}
                          </td>
                          <td className="px-3 py-2">
                            {b.lastReconciliationStatus
                              ? <Pill label={b.lastReconciliationStatus} tone={b.lastReconciliationStatus === 'FINALIZED' ? 'green' : 'grey'} />
                              : <Dash />}
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                            {b.statementClosingBalance === null ? <Dash /> : fmtAmtZero(b.statementClosingBalance)}
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                            {b.bookClosingBalance === null ? <Dash /> : fmtAmtZero(b.bookClosingBalance)}
                          </td>
                          <td className={cn(
                            AMT_CLS,
                            diffBad ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600',
                          )}>
                            {b.reconciliationDifference === null ? <Dash /> : fmtAmtZero(b.reconciliationDifference)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {b.lastCompletedDate ? fmtDate(b.lastCompletedDate) : <Dash />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Drill-down detail */}
          {detailArea && (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {AREA_LABELS[detailArea] ?? detailArea} — Document Matching Detail
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Subledger open items vs GL journals as at {fmtDate(data.asAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={matchStatus}
                    onChange={e => {
                      setMatchStatus(e.target.value);
                      drillInto(detailArea, e.target.value);
                    }}
                    className={cn(INPUT_CLS, 'w-auto')}
                  >
                    {MATCH_STATUS_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {detailLoading && <Spinner size="sm" />}
                </div>
              </div>

              {detailError && (
                <div className="m-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
                  {detailError}
                </div>
              )}

              {detail && (
                <>
                  <div className="overflow-auto">
                    <table className="w-full text-sm border-collapse min-w-[1300px]">
                      <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                        <tr>
                          <th className={TH_CLS}>Control A/C</th>
                          <th className={TH_CLS}>Subledger Doc</th>
                          <th className={TH_CLS}>Reference</th>
                          <th className={cn(TH_CLS, 'w-28')}>Doc Date</th>
                          <th className={TH_R_CLS}>Outstanding</th>
                          <th className={TH_R_CLS}>Original</th>
                          <th className={TH_CLS}>GL Journal</th>
                          <th className={cn(TH_CLS, 'w-28')}>GL Date</th>
                          <th className={TH_R_CLS}>GL Amount</th>
                          <th className={TH_R_CLS}>Difference</th>
                          <th className={cn(TH_CLS, 'w-40')}>Match</th>
                          <th className={TH_CLS}>Exception Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.items.length === 0 ? (
                          <tr>
                            <td colSpan={12} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                              No documents match the selected criteria.
                            </td>
                          </tr>
                        ) : detail.items.map((d, i) => {
                          const diffBad = hasDifference(d.difference);
                          return (
                            <tr key={`${d.subledgerDocument ?? ''}-${d.glJournalNumber ?? ''}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{d.controlAccountCode ?? <Dash />}</td>
                              <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{d.subledgerDocument ?? <Dash />}</td>
                              <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{d.subledgerReference ?? <Dash />}</td>
                              <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                {d.subledgerDate ? fmtDate(d.subledgerDate) : <Dash />}
                              </td>
                              <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                                {d.subledgerOutstandingAmount === null ? <Dash /> : fmtAmtZero(d.subledgerOutstandingAmount)}
                              </td>
                              <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                                {d.subledgerOriginalAmount === null ? <Dash /> : fmtAmtZero(d.subledgerOriginalAmount)}
                              </td>
                              <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{d.glJournalNumber ?? <Dash />}</td>
                              <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                {d.glPostingDate ? fmtDate(d.glPostingDate) : <Dash />}
                              </td>
                              <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                                {d.glAmount === null ? <Dash /> : fmtAmtZero(d.glAmount)}
                              </td>
                              <td className={cn(
                                AMT_CLS,
                                diffBad ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600',
                              )}>
                                {d.difference === null ? <Dash /> : fmtAmtZero(d.difference)}
                              </td>
                              <td className="px-3 py-2">
                                <Pill label={d.matchStatus} tone={MATCH_TONES[d.matchStatus] ?? 'grey'} />
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{d.exceptionReason ?? <Dash />}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Pager
                    pageNumber={detail.pageNumber}
                    totalPages={detail.totalPages}
                    totalCount={detail.totalCount}
                    shownCount={detail.items.length}
                    loading={detailLoading}
                    onPage={p => {
                      if (detailApplied) void fetchDetail({ ...detailApplied, pageNumber: p });
                    }}
                    noun="documents"
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
